import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiMenu, FiBell, FiUser } from 'react-icons/fi';
import api from '../../utils/api';

const AdminHeader = ({ onMenuClick }) => {
  const { user } = useSelector((state) => state.auth);
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await api.get('/notifications/admin/unread-count');
      setUnreadCount(response.data?.data?.unreadCount || 0);
    } catch (error) {
      // Keep header stable if notification service is temporarily unavailable.
      setUnreadCount(0);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();

    const refreshInterval = window.setInterval(fetchUnreadCount, 20000);
    const handleNotificationsUpdated = () => fetchUnreadCount();
    window.addEventListener('admin-notifications-updated', handleNotificationsUpdated);

    return () => {
      window.clearInterval(refreshInterval);
      window.removeEventListener('admin-notifications-updated', handleNotificationsUpdated);
    };
  }, [fetchUnreadCount]);

  const getPageTitle = () => {
    const path = location.pathname;

    if (path === '/admin' || path === '/admin/dashboard') return 'Dashboard';
    if (path === '/admin/products') return 'Products';
    if (path === '/admin/products/new') return 'Add Product';
    if (path.startsWith('/admin/products/edit/')) return 'Edit Product';
    if (path === '/admin/categories') return 'Categories';
    if (path === '/admin/hero') return 'Homepage Hero';
    if (path === '/admin/orders') return 'Orders';
    if (path.startsWith('/admin/orders/')) return 'Order Details';
    if (path === '/admin/analytics') return 'Sales Analytics';
    if (path === '/admin/notifications') return 'Notifications';
    if (path === '/admin/activity') return 'Activity Logs';
    if (path === '/admin/users') return 'Users';
    if (path === '/admin/profile') return 'Profile';

    return 'Admin';
  };

  return (
    <header className="flex items-center justify-between h-[60px] sm:h-[60px] px-3 sm:px-6 bg-white border-b border-gray-200">
      <div className="flex items-center gap-2 sm:gap-4">
        <button 
          className="lg:hidden p-2 text-xl text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
          onClick={onMenuClick}
          aria-label="Toggle menu"
        >
          <FiMenu />
        </button>
        <h1 className="text-base sm:text-xl font-semibold truncate" style={{ fontFamily: 'var(--font-body)' }}>{getPageTitle()}</h1>
      </div>

      <div className="flex items-center gap-3 sm:gap-6">
        <button
          className="relative p-1.5 sm:p-2 text-lg sm:text-xl text-gray-600"
          onClick={() => navigate('/admin/notifications')}
          title="View notifications"
        >
          <FiBell />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[14px] sm:min-w-[16px] h-3.5 sm:h-4 px-0.5 sm:px-1 text-[0.55rem] sm:text-[0.65rem] font-semibold text-white bg-red-500 rounded-full flex items-center justify-center">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        <Link to="/admin/profile" className="admin-header-profile">
          <div className="admin-header-avatar">
            {user?.avatar ? (
              <img src={user.avatar} alt={user.name} />
            ) : (
              <FiUser />
            )}
          </div>
          <div className="admin-header-userinfo">
            <span className="admin-header-username">{user?.name}</span>
            <span className="admin-header-role">Administrator</span>
          </div>
        </Link>
      </div>
    </header>
  );
};

export default AdminHeader;
